import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchManufacturerRetailersGraph } from '../../../../services/Http/ManufacturerApi';
import { SearchableSelect, SimpleSelect } from '../../../../shared/layout/forms/AppSelect';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const palette = ['#821692', '#0DCAF0', '#ff7043', '#4caf50', '#ffca28', '#26a69a', '#5c6bc0', '#ef5350', '#8d6e63', '#29b6f6'];

const periodOptions = ['yesterday', 'last_7_days', 'this_month', 'this_quarter', 'this_year'];

const RetailersGraph = () => {
    const { t } = useTranslation();
    const [period, setPeriod] = useState('last_7_days');
    const [selectedRetailers, setSelectedRetailers] = useState(['all']);
    const [chartData, setChartData] = useState({ categories: [], series: [] });
    const [retailerOptions, setRetailerOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadGraph = useCallback(async (nextPeriod, retailersFilter) => {
        const filter = Array.isArray(retailersFilter) && retailersFilter.length ? retailersFilter : ['all'];
        setLoading(true);
        try {
            const params = { period: nextPeriod };
            if (!filter.includes('all')) {
                params.retailers = filter;
            } else {
                params.retailers = 'all';
            }
            const response = await fetchManufacturerRetailersGraph(params);
            setRetailerOptions(response.retailers || []);
            setChartData({
                categories: response.categories || [],
                series: response.series || [],
            });
        } catch (error) {
            toast.error(t('ManufacturerRetailers.errors.loadGraph'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadGraph(period, selectedRetailers);
    }, [loadGraph, period, selectedRetailers]);

    const apexSeries = useMemo(() => {
        const flattened = [];
        chartData.series.forEach((series) => {
            flattened.push({
                name: `${series.retailer_name} - ${t('ManufacturerRetailers.graph.buyLabel')}`,
                data: series.sell_in,
            });
            flattened.push({
                name: `${series.retailer_name} - ${t('ManufacturerRetailers.graph.sellLabel')}`,
                data: series.sell_through,
            });
        });
        return flattened;
    }, [chartData.series, t]);

    const chartColors = useMemo(() => {
        const colors = [];
        chartData.series.forEach((series, index) => {
            const color = palette[index % palette.length];
            colors.push(color);
            colors.push(adjustAlpha(color));
        });
        return colors;
    }, [chartData.series]);

    const periodSelectOptions = useMemo(
        () =>
            periodOptions.map((value) => ({
                value,
                label: t(`ManufacturerRetailers.graph.periodOption.${value}`),
            })),
        [t]
    );

    const retailerSelectOptions = useMemo(
        () => [
            { value: 'all', label: t('ManufacturerRetailers.graph.allRetailers') },
            ...retailerOptions.map((retailer) => ({ value: retailer.id, label: retailer.name })),
        ],
        [retailerOptions, t]
    );

    const handleRetailerChange = (values) => {
        if (values.length === 0 || values.includes('all')) {
            setSelectedRetailers(['all']);
        } else {
            setSelectedRetailers(values);
        }
    };

    if (loading && chartData.series.length === 0) {
        return (
            <Centered>
                <Spinner />
            </Centered>
        );
    }

    const hasData = chartData.series.length > 0 && chartData.categories.length > 0;

    return (
        <div>
            <FilterBar>
                <FilterGroup>
                    <Label>{t('ManufacturerRetailers.graph.period')}</Label>
                    <Select
                        value={period}
                        onChange={(event, nextValue) => setPeriod(nextValue ?? event?.target?.value ?? 'last_7_days')}
                        options={periodSelectOptions}
                        inputId="manufacturer-retailers-period"
                    />
                </FilterGroup>
                <FilterGroup>
                    <Label>{t('ManufacturerRetailers.graph.retailers')}</Label>
                    <MultiSelect
                        value={selectedRetailers}
                        onChange={(event, nextValue) => handleRetailerChange(nextValue ?? event?.target?.value ?? [])}
                        options={retailerSelectOptions}
                        inputId="manufacturer-retailers-multi"
                        isMulti
                    />
                </FilterGroup>
            </FilterBar>

            {!hasData && !loading && (
                <Placeholder>{t('ManufacturerRetailers.graph.noData')}</Placeholder>
            )}

            {hasData && (
                <Suspense fallback={<Centered><Spinner /></Centered>}>
                    <ReactApexChart
                        type="line"
                        height={420}
                        series={apexSeries}
                        options={{
                            chart: { id: 'manufacturer-retailers-graph', toolbar: { show: false } },
                            xaxis: { categories: chartData.categories },
                            colors: chartColors,
                            stroke: { width: 3 },
                            legend: { position: 'top' },
                            tooltip: { shared: true, intersect: false },
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default RetailersGraph;

const FilterBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.25rem;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const Label = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary || '#333'};
`;

const Select = styled(SimpleSelect)`
    min-width: 180px;
`;

const MultiSelect = styled(SearchableSelect)`
    min-width: 280px;
`;

const Placeholder = styled.div`
    border: 1px dashed rgba(130, 22, 146, 0.4);
    border-radius: 12px;
    padding: 2.5rem;
    text-align: center;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Centered = styled.div`
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const adjustAlpha = (hex) => {
    const stripped = hex.replace('#', '');
    const bigint = parseInt(stripped, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, 0.5)`;
};
